import type { CalculatorConfig } from '../models';
import {
  KPI_CORE, KPI_E, KPI_EXT, KPI_ART,
  KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND,
  KPI_UNKNOWN_GROUND, KPI_ORDERED_GROUND,
} from '../constants';
import {
  planKpiCalculatorConfig_PlannedArticleEnabled,
  planKpiCalculatorConfig_CoreExtendedPlannedArticleEnabled,
  planKpiCalculatorConfig_CoreExtendedEnabled,
  planKpiCalculatorConfig_CoreExtendedExcitementEnabled,
  planKpiCalculatorConfig_CoreExtendedExcitementPlannedArticlesEnabled,
  groundKpiCalculatorConfig_OrderedGroundEnabled,
  groundKpiCalculatorConfig_CoreExtendedEnabled,
  groundKpiCalculatorConfig_CoreExtendedExcitementEnabled,
} from '../config/kpiCalculatorConfig';

export type KpiSelectionResult = {
  selectedPlanKpis: string[];
  selectedGroundKpis: string[];
  kpiConfig: CalculatorConfig[];
  groundKpiConfig: CalculatorConfig[];
};

// Mirrors Angular workspace's configureKpiBasedOnSelection + setKpiConfigBySelectedPlanAndGroundKpi
export function computeKpiSelection(
  isCoreExtendedChecked: boolean,
  isExcitementChecked: boolean,
  isPlannedArticlesChecked: boolean,
  isIncludeExcitementToggleChecked: boolean,
): KpiSelectionResult {
  const { selectedPlanKpis, selectedGroundKpis } = getSelectedKpis(isPlannedArticlesChecked, isCoreExtendedChecked, isExcitementChecked);
  const { kpiConfig, groundKpiConfig } = getKpiCalculatorConfig(selectedPlanKpis, isIncludeExcitementToggleChecked);
  return { selectedPlanKpis, selectedGroundKpis, kpiConfig, groundKpiConfig };
}

export function getSelectedKpis(isPlannedArticlesChecked: boolean, isCoreExtendedChecked: boolean, isExcitementChecked: boolean) {
  const selectedPlanKpis: string[] = [];
  const selectedGroundKpis: string[] = [];
  if (isPlannedArticlesChecked) {
    if (!isCoreExtendedChecked && !isExcitementChecked) {
      selectedPlanKpis.push(KPI_ART);
      selectedGroundKpis.push(KPI_ORDERED_GROUND);
    } else if (isCoreExtendedChecked && isExcitementChecked) {
      selectedPlanKpis.push(KPI_CORE, KPI_E, KPI_EXT, KPI_ART);
      selectedGroundKpis.push(KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND, KPI_UNKNOWN_GROUND);
    } else if (isCoreExtendedChecked) {
      selectedPlanKpis.push(KPI_CORE, KPI_E, KPI_ART);
      selectedGroundKpis.push(KPI_CORE_GROUND, KPI_E_GROUND, KPI_UNKNOWN_GROUND);
    }
  } else if (isCoreExtendedChecked && isExcitementChecked) {
    selectedPlanKpis.push(KPI_CORE, KPI_E, KPI_EXT);
    selectedGroundKpis.push(KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND, KPI_UNKNOWN_GROUND);
  } else if (isCoreExtendedChecked) {
    selectedPlanKpis.push(KPI_CORE, KPI_E);
    selectedGroundKpis.push(KPI_CORE_GROUND, KPI_E_GROUND, KPI_UNKNOWN_GROUND);
  } else {
    selectedPlanKpis.push(KPI_ART);
    selectedGroundKpis.push(KPI_ORDERED_GROUND);
  }
  return { selectedPlanKpis, selectedGroundKpis };
}

export function getKpiCalculatorConfig(selectedPlanKpis: string[], isIncludeExcitementToggleChecked: boolean) {
  const hasPlannedArticles = selectedPlanKpis.includes(KPI_ART);
  const hasCoreKpi = selectedPlanKpis.includes(KPI_CORE);
  const hasExtKpi = selectedPlanKpis.includes(KPI_EXT);

  if (hasPlannedArticles) {
    return getConfigWithPlannedArticles(hasCoreKpi, hasExtKpi, isIncludeExcitementToggleChecked);
  }
  return getConfigWithoutPlannedArticles(hasCoreKpi, hasExtKpi, isIncludeExcitementToggleChecked);
}

function getConfigWithPlannedArticles(
  hasCoreKpi: boolean,
  hasExtKpi: boolean,
  includeExcitement: boolean,
): { kpiConfig: CalculatorConfig[]; groundKpiConfig: CalculatorConfig[] } {
  if (!hasCoreKpi && !hasExtKpi) {
    return {
      kpiConfig: planKpiCalculatorConfig_PlannedArticleEnabled,
      groundKpiConfig: groundKpiCalculatorConfig_OrderedGroundEnabled,
    };
  }
  if (hasCoreKpi && hasExtKpi) {
    return {
      kpiConfig: includeExcitement
        ? planKpiCalculatorConfig_CoreExtendedExcitementPlannedArticlesEnabled
        : planKpiCalculatorConfig_CoreExtendedPlannedArticleEnabled,
      groundKpiConfig: includeExcitement
        ? groundKpiCalculatorConfig_CoreExtendedExcitementEnabled
        : groundKpiCalculatorConfig_CoreExtendedEnabled,
    };
  }
  return {
    kpiConfig: planKpiCalculatorConfig_CoreExtendedPlannedArticleEnabled,
    groundKpiConfig: groundKpiCalculatorConfig_CoreExtendedEnabled,
  };
}

function getConfigWithoutPlannedArticles(
  hasCoreKpi: boolean,
  hasExtKpi: boolean,
  includeExcitement: boolean,
): { kpiConfig: CalculatorConfig[]; groundKpiConfig: CalculatorConfig[] } {
  if (hasCoreKpi && hasExtKpi) {
    return {
      kpiConfig: includeExcitement
        ? planKpiCalculatorConfig_CoreExtendedExcitementEnabled
        : planKpiCalculatorConfig_CoreExtendedEnabled,
      groundKpiConfig: includeExcitement
        ? groundKpiCalculatorConfig_CoreExtendedExcitementEnabled
        : groundKpiCalculatorConfig_CoreExtendedEnabled,
    };
  }
  if (hasCoreKpi) {
    return {
      kpiConfig: planKpiCalculatorConfig_CoreExtendedEnabled,
      groundKpiConfig: groundKpiCalculatorConfig_CoreExtendedEnabled,
    };
  }
  return {
    kpiConfig: planKpiCalculatorConfig_CoreExtendedPlannedArticleEnabled,
    groundKpiConfig: groundKpiCalculatorConfig_CoreExtendedEnabled,
  };
}
